import { useState, useEffect, type FormEvent } from 'react';
import { supabase } from '../../lib/supabase';
import type { Party } from '../../types';

export default function PartiesPage() {
  const [parties, setParties] = useState<Party[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [saving, setSaving] = useState(false);

  const [editingId, setEditingId] = useState<number | null>(null);

  const [form, setForm] = useState({
    name: '',
    address1: '',
    address2: '',
    address3: '',
    contact_person: '',
    phone: '',
    email: '',
    gst: '',
  });

  const PAGE_SIZE = 50;

  useEffect(() => {
    fetchParties();
  }, []);

  async function fetchParties() {
    setLoading(true);
    setError(null);

    try {
      let allParties: Party[] = [];
      let from = 0;
      while (true) {
        const { data, error } = await supabase
          .from('parties')
          .select('*')
          .range(from, from + 999)
          .order('name', { ascending: true });

        if (error) {
          setError(error.message);
          return;
        }

        allParties = allParties.concat(data || []);
        if (!data || data.length < 1000) break;
        from += 1000;
      }

      setParties(allParties);
    } catch (err) {
      setError('Failed to fetch parties');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  function openAddModal() {
    setEditingId(null);
    setForm({
      name: '',
      address1: '',
      address2: '',
      address3: '',
      contact_person: '',
      phone: '',
      email: '',
      gst: '',
    });
    setShowAddModal(true);
  }

  function openEditModal(party: Party) {
    setEditingId(party.id || null);
    setForm({
      name: party.name,
      address1: party.address1 || '',
      address2: party.address2 || '',
      address3: party.address3 || '',
      contact_person: party.contact_person || '',
      phone: party.phone || '',
      email: party.email || '',
      gst: party.gst || '',
    });
    setShowAddModal(true);
  }

  async function handleDeleteParty(id: number, name: string) {
    if (!window.confirm(`Are you sure you want to delete customer account "${name}"? This action cannot be undone.`)) {
      return;
    }

    setLoading(true);
    const { error: delErr } = await supabase.from('parties').delete().eq('id', id);

    if (delErr) {
      setError(delErr.message);
      setLoading(false);
      return;
    }

    fetchParties();
  }

  async function handleSaveParty(e: FormEvent) {
    e.preventDefault();
    if (!form.name.trim()) return;

    setSaving(true);
    setError(null);

    const partyPayload = {
      name: form.name.trim(),
      address1: form.address1.trim() || undefined,
      address2: form.address2.trim() || undefined,
      address3: form.address3.trim() || undefined,
      contact_person: form.contact_person.trim() || undefined,
      phone: form.phone.trim() || undefined,
      email: form.email.trim() || undefined,
      gst: form.gst.trim() || undefined,
    };

    if (editingId) {
      const { error: updateErr } = await supabase
        .from('parties')
        .update(partyPayload)
        .eq('id', editingId);

      setSaving(false);
      if (updateErr) {
        setError(updateErr.message);
        return;
      }
    } else {
      const { error: insertErr } = await supabase.from('parties').insert([partyPayload]);

      setSaving(false);
      if (insertErr) {
        setError(insertErr.message);
        return;
      }
    }

    setForm({
      name: '',
      address1: '',
      address2: '',
      address3: '',
      contact_person: '',
      phone: '',
      email: '',
      gst: '',
    });
    setEditingId(null);
    setShowAddModal(false);
    fetchParties();
  }

  const filteredParties = parties.filter((p) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      p.name.toLowerCase().includes(q) ||
      (p.contact_person && p.contact_person.toLowerCase().includes(q)) ||
      (p.phone && p.phone.toLowerCase().includes(q)) ||
      (p.email && p.email.toLowerCase().includes(q)) ||
      (p.gst && p.gst.toLowerCase().includes(q)) ||
      (p.address1 && p.address1.toLowerCase().includes(q)) ||
      (p.address3 && p.address3.toLowerCase().includes(q))
    );
  });

  const totalPages = Math.ceil(filteredParties.length / PAGE_SIZE) || 1;
  const paginatedParties = filteredParties.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  return (
    <div className="space-y-6">
      {/* Page Header Bar */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-black tracking-tight text-slate-900">Customer Parties Master</h1>
          <p className="mt-0.5 text-xs text-slate-500">
            Deduplicated corporate client database ({parties.length} verified records across all branches)
          </p>
        </div>
        <button
          onClick={openAddModal}
          className="bg-blue-600 hover:bg-blue-700 active:scale-95 text-white text-xs sm:text-sm font-bold px-4 py-2.5 rounded-xl shadow-md shadow-blue-600/20 transition-all flex items-center gap-2 cursor-pointer whitespace-nowrap"
        >
          <span>+</span>
          <span>Add New Customer</span>
        </button>
      </div>

      {/* Main Table Card Container */}
      <div className="bg-white rounded-2xl shadow-card border border-slate-200/80 overflow-hidden">
        {/* Search Bar & Statistics Header */}
        <div className="p-4 sm:p-5 border-b border-slate-200/80 bg-slate-50/60 flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
          <div className="w-full sm:w-96 relative">
            <input
              type="text"
              placeholder="Search by party name, contact person, phone, city, or GSTIN..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-9 py-2.5 text-xs sm:text-sm bg-white border border-slate-300 rounded-xl focus:border-blue-500 shadow-sm text-slate-900 placeholder-slate-400 transition-colors"
            />
            <span className="absolute left-3.5 top-3 text-slate-400 text-sm">🔍</span>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600 text-xs font-bold p-0.5"
              >
                ✕
              </button>
            )}
          </div>

          <div className="text-xs font-medium text-slate-500 flex items-center gap-2">
            <span>
              Showing <strong className="text-slate-800">{paginatedParties.length > 0 ? (currentPage - 1) * PAGE_SIZE + 1 : 0}</strong> to{' '}
              <strong className="text-slate-800">{Math.min(currentPage * PAGE_SIZE, filteredParties.length)}</strong> of{' '}
              <strong className="text-slate-800">{filteredParties.length}</strong> matches
            </span>
          </div>
        </div>

        {/* Loading Spinner */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-16 text-slate-500 space-y-3">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent"></div>
            <span className="text-xs font-semibold text-slate-600">Loading master party database...</span>
          </div>
        )}

        {/* Error Banner */}
        {error && (
          <div className="m-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-xs font-medium">
            <p className="font-bold">Error loading database</p>
            <p className="mt-0.5">{error}</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && paginatedParties.length === 0 && (
          <div className="py-16 text-center text-slate-400 space-y-2">
            <span className="text-3xl">👥</span>
            <p className="text-sm font-bold text-slate-700">No customer records match your search</p>
            <p className="text-xs text-slate-500">Try searching by partial name, state, or 15-character GSTIN.</p>
          </div>
        )}

        {/* Table Content */}
        {!loading && !error && paginatedParties.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs divide-y divide-slate-200">
              <thead className="bg-slate-50/80 text-slate-500 font-bold uppercase tracking-wider text-[11px]">
                <tr>
                  <th className="px-5 py-3.5">Customer / Company Name</th>
                  <th className="px-4 py-3.5">Contact Person</th>
                  <th className="px-4 py-3.5">Phone Number</th>
                  <th className="px-4 py-3.5">Email</th>
                  <th className="px-4 py-3.5">Location & Address</th>
                  <th className="px-4 py-3.5">GSTIN</th>
                  <th className="px-5 py-3.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {paginatedParties.map((party) => (
                  <tr key={party.id} className="hover:bg-slate-50/70 transition-colors">
                    <td className="px-5 py-3.5">
                      <div className="font-bold text-slate-900 text-xs">{party.name}</div>
                    </td>
                    <td className="px-4 py-3.5 text-slate-600 font-medium">
                      {party.contact_person || <span className="text-slate-300">-</span>}
                    </td>
                    <td className="px-4 py-3.5 text-slate-600 font-mono text-[11px]">
                      {party.phone || <span className="text-slate-300">-</span>}
                    </td>
                    <td className="px-4 py-3.5 text-slate-500 max-w-[150px] truncate">
                      {party.email || <span className="text-slate-300">-</span>}
                    </td>
                    <td className="px-4 py-3.5 text-slate-600 max-w-[200px] truncate">
                      {[party.address1, party.address2, party.address3].filter(Boolean).join(', ') || (
                        <span className="text-slate-300">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3.5">
                      {party.gst ? (
                        <span className="font-mono text-[10px] font-bold px-2 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200">
                          {party.gst}
                        </span>
                      ) : (
                        <span className="text-slate-300">-</span>
                      )}
                    </td>
                    <td className="px-5 py-3.5 text-right whitespace-nowrap space-x-1.5">
                      <button
                        onClick={() => openEditModal(party)}
                        className="px-2.5 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 font-bold rounded-lg text-xs transition-colors"
                        title="Edit Customer Profile"
                      >
                        Edit ✏️
                      </button>
                      <button
                        onClick={() => party.id && handleDeleteParty(party.id, party.name)}
                        className="px-2 py-1 bg-red-50 hover:bg-red-100 text-red-700 font-bold rounded-lg text-xs transition-colors"
                        title="Delete Customer Account"
                      >
                        Delete 🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls */}
        {!loading && totalPages > 1 && (
          <div className="px-5 py-4 border-t border-slate-200/80 flex items-center justify-between bg-slate-50/60 text-xs">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1.5 border border-slate-300 rounded-lg font-bold text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-40 transition-colors cursor-pointer"
            >
              ← Previous
            </button>
            <div className="text-slate-600 font-semibold">
              Page <span className="font-bold text-slate-900">{currentPage}</span> of{' '}
              <span className="font-bold text-slate-900">{totalPages}</span>
            </div>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1.5 border border-slate-300 rounded-lg font-bold text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-40 transition-colors cursor-pointer"
            >
              Next →
            </button>
          </div>
        )}
      </div>

      {/* Add / Edit Customer Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 sm:p-7 space-y-5 shadow-2xl border border-slate-200">
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-blue-600">
                  {editingId ? 'Update Record' : 'New Client'}
                </span>
                <h3 className="text-lg font-bold text-slate-900">
                  {editingId ? 'Edit Customer Party' : 'Add New Customer Party'}
                </h3>
              </div>
              <button
                onClick={() => {
                  setEditingId(null);
                  setShowAddModal(false);
                }}
                className="text-slate-400 hover:text-slate-600 font-bold text-lg p-1 rounded-lg hover:bg-slate-100"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSaveParty} className="space-y-4 text-xs">
              {/* Section 1: Business Name */}
              <div>
                <label className="block font-bold text-slate-700 mb-1">
                  Company / Legal Business Name <span className="text-red-500">*</span>
                </label>
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs focus:border-blue-500"
                  placeholder="e.g. Acme Spinning Mills Ltd"
                />
              </div>

              {/* Section 2: Contact Person & Phone */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Contact Person</label>
                  <input
                    value={form.contact_person}
                    onChange={(e) => setForm({ ...form, contact_person: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs focus:border-blue-500"
                    placeholder="Mr. Sharma"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Phone Number</label>
                  <input
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs focus:border-blue-500 font-mono"
                    placeholder="98765-43210"
                  />
                </div>
              </div>

              {/* Section 3: Email & GSTIN */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Email Address</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs focus:border-blue-500"
                    placeholder="purchase@acme.com"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">GSTIN Number</label>
                  <input
                    value={form.gst}
                    onChange={(e) => setForm({ ...form, gst: e.target.value.toUpperCase() })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs focus:border-blue-500 font-mono"
                    placeholder="03ACQFS9127A1Z6"
                  />
                </div>
              </div>

              {/* Section 4: Address Details */}
              <div>
                <label className="block font-bold text-slate-700 mb-1">Address Line 1</label>
                <input
                  value={form.address1}
                  onChange={(e) => setForm({ ...form, address1: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs focus:border-blue-500"
                  placeholder="Plot 45, Focal Point Phase II"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Area / Landmark</label>
                  <input
                    value={form.address2}
                    onChange={(e) => setForm({ ...form, address2: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs focus:border-blue-500"
                    placeholder="Near Eastman Chowk"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">City, State & PIN</label>
                  <input
                    value={form.address3}
                    onChange={(e) => setForm({ ...form, address3: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs focus:border-blue-500"
                    placeholder="Ludhiana - 141010"
                  />
                </div>
              </div>

              {/* Modal Actions */}
              <div className="flex justify-end gap-2 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => {
                    setEditingId(null);
                    setShowAddModal(false);
                  }}
                  className="px-4 py-2 border border-slate-300 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold shadow-sm disabled:opacity-50 transition-colors cursor-pointer"
                >
                  {saving ? 'Saving...' : editingId ? 'Update Customer' : 'Save Customer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
